/**
 * Krok AI9 bod 2 — zakázky bez pohybu pro „Ekonomiku kanceláře" (owner/admin).
 *
 * `staleCases` (`@reos/today-metrics`) je JEDNA funkce pro „kolik dní je
 * zakázka beze změny" — stejná jako `isCaseInMotion` na Dnešku, jen jiný prah
 * (14 dní místo 7) a jiný směr (chybějící pohyb, ne přítomný). Tady se jen
 * stáhnou VŠECHNY zakázky kanceláře (ne jen vlastní — `listCases` bez
 * `ownerUserId` vrací owner/adminovi dosah celého tenantu, viz
 * `packages/case/src/public/plugin.ts` `brokerOfficeScopeFilter`) a vyloučí
 * se uzavřené/zrušené, u kterých je „beze změny 14 dní" normální stav, ne
 * problém k řešení.
 */

import { staleCases } from '@reos/today-metrics';
import { CANCELLED_STAGE } from './case-stages.js';
import { CASE_SCAN_MAX_PAGES, listCases, type KanbanCase } from './cases-api.js';
import { amountBucket, invoiceStats } from './invoice-stats.js';
import type { InvoiceDto } from './invoices-api.js';
import type { AdvertisingCostsSummaryRow } from './advertising-api.js';

const CLOSED_STAGE = 'closed';
/**
 * Všechny AKTIVNÍ zakázky kanceláře (bez uzavřených a zrušených), stránkované.
 * Strop stran je `CASE_SCAN_MAX_PAGES` — stejný jako u ostatních průchodů
 * seznamem zakázek; vzestupné stránkování jinak urve právě nejnovější zakázky.
 */
export async function fetchActiveCases(): Promise<readonly KanbanCase[]> {
  const items: KanbanCase[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < CASE_SCAN_MAX_PAGES; page += 1) {
    const result = await listCases({ limit: 100, ...(cursor !== null ? { cursor } : {}) });
    items.push(...result.items);
    if (result.nextCursor === null) break;
    cursor = result.nextCursor;
  }
  return items.filter((item) => item.stage !== CANCELLED_STAGE && item.stage !== CLOSED_STAGE);
}

/**
 * Krok UXK3c bod 3 — ČTYŘI ČÍSLA NAHOŘE na „Ekonomice kanceláře".
 *
 * Zadání: provize tento měsíc, otevřené zakázky, faktury po splatnosti,
 * náklady na inzerci — VŠE z dat, která už admin umí načíst. Žádný nový
 * endpoint a žádný druhý součet peněz: částky sčítá `amountBucket`
 * z `invoice-stats.ts` (jediné místo, kde se v adminu sčítají haléře)
 * a počet dokladů po splatnosti je `invoiceStats(...).overdue.count`
 * — tedy TÉŽ číslo, jaké ukazuje dlaždice na `/fakturace`.
 *
 * „Když data nejsou, položku skrýt" je tady `null`: položka se do výsledku
 * vůbec nedostane. Nula je legitimní hodnota JEN tam, kde je výpovědní
 * (otevřené zakázky, doklady po splatnosti); prázdný součet peněz by jako
 * „0 Kč" lhal — znamená „nemáme co sečíst", ne „vydělali jsme nic".
 */
export type OfficeEconomyKey = 'commissionThisMonth' | 'openCases' | 'overdueInvoices' | 'adSpend';

export interface OfficeEconomyItem {
  readonly key: OfficeEconomyKey;
  /** Počet (zakázky, doklady), nebo `null` u peněžních položek. */
  readonly count: number | null;
  /** Součet jako desetinný řetězec, nebo `null` u počtů. */
  readonly total: string | null;
  readonly currency: string | null;
}

/** Měsíc částky/dokladu jako `YYYY-MM`. */
function month(date: Date): string {
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function officeEconomySummary(
  input: {
    readonly cases: readonly KanbanCase[];
    readonly invoices: readonly InvoiceDto[];
    readonly adCosts: readonly AdvertisingCostsSummaryRow[];
  },
  now: Date,
): readonly OfficeEconomyItem[] {
  const thisMonth = month(now);
  // Provize = doklad se `source: 'commission'`, vystavený nebo zaplacený
  // v tomhle měsíci. Koncept ani storno se nepočítá — tytéž stavy, jaké
  // vynechává `invoiceStats` (`OPEN_STATUS` / `paid`).
  const commission = input.invoices.filter(
    (invoice) =>
      invoice.source === 'commission' &&
      (invoice.status === 'issued' || invoice.status === 'paid') &&
      invoice.issueDate.slice(0, 7) === thisMonth,
  );
  const provize = amountBucket(commission);
  const inzerce = amountBucket(
    input.adCosts.map((row) => ({ totalAmount: row.total, currency: row.currency })),
  );
  const poSplatnosti = invoiceStats(input.invoices, now).overdue;

  const items: (OfficeEconomyItem | null)[] = [
    provize.total === null
      ? null
      : { key: 'commissionThisMonth', count: null, total: provize.total, currency: provize.currency },
    input.cases.length === 0
      ? null
      : { key: 'openCases', count: input.cases.length, total: null, currency: null },
    // Nula dokladů po splatnosti je dobrá zpráva, ne chybějící data —
    // položka se skrývá jen tehdy, když kancelář nemá doklady vůbec.
    input.invoices.length === 0
      ? null
      : { key: 'overdueInvoices', count: poSplatnosti.count, total: null, currency: null },
    inzerce.total === null
      ? null
      : { key: 'adSpend', count: null, total: inzerce.total, currency: inzerce.currency },
  ];
  return items.filter((item): item is OfficeEconomyItem => item !== null);
}

/** Zakázky bez pohybu 14 dní, nejdéle nehybné první. */
export function stalledCasesOverview(
  cases: readonly KanbanCase[],
  now: Date,
): readonly KanbanCase[] {
  return [...staleCases(cases, now)].sort(
    (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
  );
}
